/**
 * Whether a draft still differs from the record it was opened on.
 *
 * A sheet editing a record keeps its commit disabled until there is something
 * to commit, and shows it on EVERY tab: what is unsaved belongs to the record,
 * not to the area you happen to be reading. A Save that lives on one tab
 * teaches that the others do not save, while the draft they edit is the same
 * draft.
 *
 * Compared by value, because a draft is a flat object of primitives rebuilt on
 * every keystroke: comparing identity would say "changed" from the first
 * keystroke and never say otherwise, including after typing a letter and
 * deleting it again.
 *
 * **Key ORDER is not a change.** The two sides are written as two object
 * literals at two places in a component, so a field added to one of them lands
 * in a different position from the other — and a comparison that read the
 * literal order would call the form dirty the moment it opened, showing a Save
 * for an edit nobody made. The keys are sorted before they are compared, so
 * only values decide.
 *
 * A plain function rather than a hook — it holds no state and runs no effect,
 * and both sides are rebuilt each render, so there is nothing a hook could
 * memoise.
 */
export function hasUnsavedChanges<T>(opened: T, draft: T): boolean {
  return stable(opened) !== stable(draft)
}

/** JSON with object keys in a fixed order, at every depth. */
function stable(value: unknown): string {
  return JSON.stringify(value, (_key, v) =>
    v && typeof v === 'object' && !Array.isArray(v)
      ? Object.fromEntries(
          Object.entries(v as Record<string, unknown>).sort(([a], [b]) =>
            a < b ? -1 : a > b ? 1 : 0,
          ),
        )
      : v,
  )
}
